import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, FileCheck, Users, Lightbulb } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

export const TrainingSection = () => {
  const { t, language } = useLanguage();
  const trainingPath = language === 'fr' ? '/formation-ia-avocat' : '/legal-ai-training-lawyers';

  const trainingItems = [{
    title: language === 'fr' ? 'Introduction à l\'IA Juridique' : 'Introduction to Legal AI',
    duration: language === 'fr' ? '1 jour' : '1 day',
    icon: GraduationCap,
    link: trainingPath
  }, {
    title: language === 'fr' ? 'Prompt Engineering Avancé' : 'Advanced Prompt Engineering',
    duration: language === 'fr' ? '2 jours' : '2 days',
    icon: Lightbulb,
    link: trainingPath
  }, {
    title: language === 'fr' ? 'Formation Cabinet Sur-Mesure' : 'Custom Firm Training',
    duration: language === 'fr' ? 'Flexible' : 'Flexible',
    icon: Users,
    link: trainingPath
  }, {
    title: language === 'fr' ? 'Audit Stratégie IA' : 'AI Strategy Audit',
    duration: language === 'fr' ? 'Sur devis' : 'On request',
    icon: FileCheck,
    link: '/audit'
  }];

  return (
    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-4">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary mb-3">
            {language === 'fr' ? 'Notre offre' : 'Our Offer'}
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            {t('servicesTitle')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('servicesSubtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 mt-12">
          {trainingItems.map((item, index) => (
            <Card key={index} className="text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-card border-border/50 flex flex-col h-full group">
              <CardHeader className="flex-1 pb-2">
                <div className="w-14 h-14 mx-auto mb-5 rounded-2xl gradient-bg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <item.icon className="w-7 h-7 text-primary-foreground" />
                </div>
                <CardTitle className="text-lg text-foreground leading-snug">{item.title}</CardTitle>
              </CardHeader>
              <CardContent className="mt-auto pt-2">
                <Button asChild variant="outline" className="w-full group-hover:gradient-bg group-hover:text-primary-foreground group-hover:border-transparent transition-all duration-300">
                  <Link to={item.link}>
                    {language === 'fr' ? `Découvrir` : `Discover`}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center relative inline-block mx-auto w-full flex justify-center">
          <div className="relative">
            <Button size="lg" className="cursor-not-allowed pointer-events-none text-lg h-14 px-10 gradient-bg text-primary-foreground opacity-70" disabled>
              <span className="flex items-center space-x-2">
                <span>{language === 'fr' ? 'Découvrir les formations éligibles aux financements' : 'Discover funded training programs'}</span>
                <ArrowRight className="w-5 h-5" />
              </span>
            </Button>
            <Badge className="absolute -top-3 -right-3 bg-accent text-accent-foreground text-[10px] px-2 py-0.5 shadow-md animate-float">
              {language === 'fr' ? 'Bientôt disponible' : 'Soon'}
            </Badge>
          </div>
        </div>
      </div>
    </section>
  );
};
